import { ReactElement, ReactNode, useEffect } from 'react';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import Script from 'next/script';
import { ConfigProvider, message } from 'antd';
import { OnboardingContextProvider } from 'context/OnboardingContext';
import { PostHogProvider } from 'posthog-js/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import {
  Provider as ReduxProvider,
  useDispatch,
  useSelector,
} from 'react-redux';
import { ThemeProvider } from 'styled-components';

import ErrorBoundary from '@components/ErrorBoundary';
import configureAmplify from '@lib/amplify.lib';
import { ClarityLifecycle } from '@lib/analytics/ClarityLifecycle';
import { PostHogAnalyticsLifecycle } from '@lib/analytics/PostHogAnalyticsLifecycle';
import { AuthProvider } from '@lib/auth';
import { withErrorHandling } from '@lib/errors';
import { posthog } from '@lib/posthog.lib';
import { store } from '@lib/redux.lib';
import { setGoogleMapsApiLoaded } from '@lib/redux.lib/slices/app/appSlice';
import type { RootState } from '@lib/redux.lib/store';
import antTheme from '@styles/antTheme';
import theme from '@styles/theme';

import ProtectRoute from './auth/protect';

import '@styles/global.css';
import '@styles/overlays.css';
import '@styles/reset.css';
import 'antd/dist/reset.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-image-crop/dist/ReactCrop.css';

configureAmplify();

// eslint-disable-next-line @typescript-eslint/ban-types
export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
  isPublic?: boolean;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
  faviconLogoUrl?: string;
  logoUrl?: string;
  clientName?: string;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const GoogleMapsApi = () => {
  const { loaded } = useSelector((state: RootState) => state.app.google);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!loaded) dispatch(setGoogleMapsApiLoaded(true));
  }, []);

  if (loaded) return <></>;

  return (
    <Script
      type='text/javascript'
      src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&libraries=places&callback=Function.prototype&loading=async`}
      onError={() => {
        message.error(
          'Google Maps API failed to load. Please refresh the page.'
        );
      }}
    />
  );
};

const CastingApp = ({
  Component,
  pageProps,
}: AppPropsWithLayout): ReactElement => {
  const routeProtected = !Component?.isPublic;
  const getLayout = Component?.getLayout || ((page: ReactElement) => page);
  const View = withErrorHandling((props) => <Component {...props} />);
  const Page: ReactNode = getLayout(<View {...pageProps} />);

  return (
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <ReduxProvider store={store}>
            <OnboardingContextProvider>
              <AuthProvider>
                <ErrorBoundary>
                  <ConfigProvider theme={antTheme}>
                    <PostHogAnalyticsLifecycle />
                    <ClarityLifecycle />
                    <ProtectRoute routeProtected={routeProtected}>
                      <GoogleMapsApi />
                      <main>{Page}</main>
                    </ProtectRoute>
                  </ConfigProvider>
                </ErrorBoundary>
              </AuthProvider>
            </OnboardingContextProvider>
          </ReduxProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </PostHogProvider>
  );
};

export default CastingApp;
